import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { SmileIcon } from '@/components/atoms/Icons/SmileIcon';
import {
  getGoalPercentage,
  getSentimentDisplay,
} from '@/components/organisms/SessionsInfiniteTable/utils/sessionUtils';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { PanelSectionTitle } from '@/modules/tryIt/shared/InfoRow';

import { InsightsList } from './InsightsList';
import { NoRatingEmpty } from './NoRatingEmpty';
import { ReactionsList } from './ReactionsList';
import { StarRating } from './StarRating';
import { useSessionContent } from '../../../hooks/useSessionContent';
import {
  selectSessionStart,
  selectThumbsDown,
  selectThumbsUp,
  selectTryItSessionId,
} from '../../../redux/selectors';

const renderGoalLabel = (goal: string, t: TFunction) => {
  if (goal === '1') {
    return t('analytics.goal_achieved');
  }
  if (goal === '2') {
    return t('analytics.goal_partially_achieved');
  }
  return t('analytics.goal_not_achieved');
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs leading-4 font-medium text-muted-foreground">{label}</span>
    {children}
  </div>
);

const renderSentimentLabel = (variant: 'happy' | 'neutral' | 'sad', t: TFunction) => {
  if (variant === 'happy') {
    return t('analytics.positive');
  }
  if (variant === 'sad') {
    return t('analytics.negative');
  }
  return t('analytics.neutral');
};

/** Replay-only Review panel. */
export const ReviewPanel = () => {
  const { t } = useTranslation();
  const sessionId = useSelector(selectTryItSessionId);
  const sessionStart = useSelector(selectSessionStart);
  const thumbsUp = useSelector(selectThumbsUp);
  const thumbsDown = useSelector(selectThumbsDown);
  const { data: content, isLoading } = useSessionContent(sessionId, sessionStart);

  const userRating = content?.rating ?? null;
  const aiRating = content?.csat_brain ?? null;
  const feedback = content?.feedback;

  const goal =
    content?.goal_achieved && content.goal_achieved !== '0' ? content.goal_achieved : null;
  const sentimentDisplay = getSentimentDisplay(content?.user_sentiment ?? null, t);
  const hasReactions = thumbsUp.length > 0 || thumbsDown.length > 0;
  const hasInsights = content?.insights != null && Object.keys(content.insights).length > 0;
  const summary = content?.conversation_summary;

  const hasUserFeedback = userRating != null || !!feedback || hasReactions;
  const hasAiAnalysis = aiRating != null || sentimentDisplay != null || goal != null || !!summary;

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (!hasUserFeedback && !hasAiAnalysis && !hasInsights) {
    return <NoRatingEmpty />;
  }

  const feedbackText = feedback ? (
    <p className="text-sm leading-5 font-normal text-foreground">{feedback}</p>
  ) : null;

  return (
    <div className="flex w-full flex-col gap-6">
      {hasUserFeedback && (
        <div className="flex flex-col gap-3">
          <PanelSectionTitle>{t('try_it.review.user_feedback')}</PanelSectionTitle>
          <div className="flex flex-col gap-4">
            {userRating != null ? (
              <Field label={t('analytics.fields.rating')}>
                <div className="flex flex-col gap-1.5">
                  <StarRating value={userRating} variant="user" />
                  {feedbackText}
                </div>
              </Field>
            ) : (
              feedbackText
            )}
            <ReactionsList />
          </div>
        </div>
      )}

      {hasAiAnalysis && (
        <>
          {hasUserFeedback && <Separator />}
          <div className="flex flex-col gap-3">
            <PanelSectionTitle>{t('try_it.review.ai_analysis')}</PanelSectionTitle>
            <div className="flex flex-col gap-4">
              {aiRating != null && (
                <Field label={t('analytics.fields.rating')}>
                  <StarRating value={aiRating} variant="ai" />
                </Field>
              )}

              {sentimentDisplay && (
                <Field label={t('try_it.review.user_sentiment')}>
                  <div className="flex items-center gap-2">
                    <SmileIcon variant={sentimentDisplay.variant} size={16} />
                    <span className="text-xs leading-4 font-medium text-foreground">
                      {renderSentimentLabel(sentimentDisplay.variant, t)}
                    </span>
                  </div>
                </Field>
              )}

              {goal && (
                <Field label={t('try_it.review.goal_metric')}>
                  <div className="flex items-center gap-2">
                    <Progress
                      className="w-40 bg-secondary"
                      value={getGoalPercentage(goal, t).value}
                    />
                    <span className="text-xs leading-4 font-medium text-foreground">
                      {renderGoalLabel(goal, t)}
                    </span>
                  </div>
                </Field>
              )}

              {summary && (
                <Field label={t('try_it.review.conversation_summary')}>
                  <p className="text-sm leading-5 font-normal text-foreground">{summary}</p>
                </Field>
              )}
            </div>
          </div>
        </>
      )}

      {hasInsights && (
        <>
          {(hasUserFeedback || hasAiAnalysis) && <Separator />}
          <InsightsList insights={content?.insights} sessionId={sessionId} />
        </>
      )}
    </div>
  );
};
